import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { getDataAttributes } from '../../../util/getDataAttributes/getDataAttributes.js';
import { extractMarginsAndPaddings } from '../../../util/spacing.js';
import '../../../util/styles/index.js';
import { Box } from '../../Box/Box.js';
import { OverlayStyled, InputContainerStyled, InputStyled } from './Input.styled.js';
import inputInteractiveState from './inputInteractiveState.js';
import { Ripple } from '../Ripple/Ripple.js';
import { useExperimentContext } from '../../../contexts/session/Experiment.js';
import { useThemeContext } from '../../../contexts/session/Theme.js';
import { extractFlexItemProps } from '../../../types/Props.js';
import { useInputState } from './useInputState.js';
import { useAdsStyles } from '../../../util/styles/hooks/useAdsStyles.js';

var _excluded = ["inputType", "label", "onInputChange", "control", "children", "state", "selection", "onChange", "decorative"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var RIPPLE_DURATION_SECONDS = 0.5;

/** INTERNAL component for reusing in other HTML input-like components. */
var Input = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var inputType = _ref.inputType,
    label = _ref.label,
    onInputChange = _ref.onInputChange,
    control = _ref.control,
    children = _ref.children,
    state = _ref.state,
    selection = _ref.selection,
    onChange = _ref.onChange,
    decorative = _ref.decorative,
    restProps = _objectWithoutProperties(_ref, _excluded);
  var theme = useThemeContext();
  var _useExperimentContext = useExperimentContext(),
    radioCheckboxFocusStateContrastFix = _useExperimentContext.radioCheckboxFocusStateContrastFix;
  var _useInputState = useInputState({
      state: state,
      selection: selection,
      onChange: onChange,
      onInputChange: onInputChange
    }),
    disabled = _useInputState.disabled,
    inputRef = _useInputState.inputRef,
    onInputChangeHandler = _useInputState.onInputChangeHandler,
    textColor = _useInputState.textColor,
    checked = _useInputState.checked;
  var _getDataAttributes = getDataAttributes(restProps),
    dataAttributes = _getDataAttributes.dataAttributes,
    remainingProps = _getDataAttributes.rest;
  var _extractMarginsAndPad = extractMarginsAndPaddings(remainingProps),
    margins = _extractMarginsAndPad.margins,
    paddings = _extractMarginsAndPad.paddings,
    remainingRestProps = _extractMarginsAndPad.rest;
  var _extractFlexItemProps = extractFlexItemProps(remainingRestProps),
    flexItemProps = _extractFlexItemProps.flexItemProps,
    rest = _extractFlexItemProps.rest;
  var inputControlStyle = control.styled,
    inputControlSize = control.size,
    inputControlComponent = control.component;
  var interactiveState = disabled ? undefined : inputInteractiveState(theme, inputControlStyle, OverlayStyled);
  var cursor = disabled ? 'auto' : 'pointer';
  var _useAdsStyles = useAdsStyles(),
    getInteractiveState = _useAdsStyles.getInteractiveState;
  var _getInteractiveState = getInteractiveState({
      type: 'container',
      within: true
    }),
    boxProps = _getInteractiveState.boxProps;
  var inputContainerBorderRadius = function inputContainerBorderRadius() {
    if (inputType === 'radio') {
      return 'max';
    }
    return '2';
  };
  var input = /*#__PURE__*/React.createElement(InputContainerStyled, _extends({}, radioCheckboxFocusStateContrastFix ? _objectSpread({}, boxProps) : {
    $interactiveState: interactiveState
  }, {
    minWidth: inputControlSize,
    width: inputControlSize,
    minHeight: inputControlSize,
    height: inputControlSize,
    position: "relative",
    cursor: cursor,
    inert: decorative ? '' : undefined,
    borderRadius: radioCheckboxFocusStateContrastFix ? inputContainerBorderRadius() : undefined
  }, flexItemProps, children ? {
    me: '8'
  } : _objectSpread({
    ref: ref
  }, margins || {})), /*#__PURE__*/React.createElement(Ripple, {
    size: "32px",
    duration: RIPPLE_DURATION_SECONDS
  }, /*#__PURE__*/React.createElement(InputStyled, _extends({
    "aria-label": label,
    type: inputType,
    checked: checked,
    disabled: disabled,
    onChange: onInputChangeHandler,
    ref: inputRef
  }, !children && _objectSpread({}, dataAttributes), rest)), inputControlComponent, !disabled && /*#__PURE__*/React.createElement(OverlayStyled, {
    borderRadius: "base",
    backgroundColor: "product.primary"
  })));
  return children ? /*#__PURE__*/React.createElement(Box, _extends({
    as: "label",
    display: "flex",
    cursor: cursor,
    textColor: textColor
  }, flexItemProps, margins, paddings, {
    ref: ref
  }, dataAttributes), input, children) : input;
});
Input.displayName = 'Input';
Input.defaultProps = {
  state: 'enabled',
  selection: 'unselected'
};

export { Input };
//# sourceMappingURL=Input.js.map
